import React, { useMemo } from 'react';
import { Box, Container, ColumnLayout, Header, Spinner, Alert } from '@amzn/awsui-components-core';
import type Highcharts from 'highcharts';
import { HighchartsChart } from '../charts/HighchartsChart';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { useMultiLeaderHeadcount } from '../useConnectionsData';
import { ConnectionsScorecard } from '../types';
import { buildPeopleFlows, connectionScore, median, latestYear, MonthValue, LeaderPeopleFlow } from './headcountUtils';
import { CONNECTIONS_COLORS, CHART_SERIES_PALETTE } from '../connectionsColors';
import { getBadgeUrl } from '../connectionsBadge';
import { ChartInfoPopover } from './ChartInfoPopover';

interface HeadcountInsightsProps {
  /** Focus leader + their directs (same set fed to the Sentiment Deep Dive). */
  leaders: Array<{ login: string; name: string }>;
  /** Connection scorecards for those leaders (directs drill-down + focus), for the join. */
  scorecards: ConnectionsScorecard[];
  /** Optional ISO start-date lower bound; headcount is monthly so we default to full history. */
  startDate?: string;
  sectionContext?: string;
  infoLink?: React.ReactNode;
}

// Chart colors are centralized in connectionsColors.ts (CONNECTIONS_COLORS / CHART_SERIES_PALETTE).
const NEG = CONNECTIONS_COLORS.NEGATIVE;
const POS = CONNECTIONS_COLORS.POSITIVE;
const ACCENT = CONNECTIONS_COLORS.ACCENT;
const LABEL = CONNECTIONS_COLORS.AXIS_LABEL;
const SERIES_PALETTE = CHART_SERIES_PALETTE;
const MAX_LINES = 8;
/** Uniform chart height so both cards in a grid row are the same size. */
const CHART_H = 360;

/** Photo + name label (matches CrossTeamComparison / "Theme scores by leader"). Used in Highcharts useHTML labels. */
const avatarHTML = (login: string, name: string): string =>
  `<img src="${getBadgeUrl(
    login
  )}" width="20" height="20" style="border-radius:50%;object-fit:cover;border:1px solid rgba(0,0,0,0.12);vertical-align:middle;margin-right:6px" onerror="this.style.display='none'" alt="" />` +
  `<span style="vertical-align:middle">${name}</span>`;

const photoLegend: Highcharts.LegendOptions = {
  useHTML: true,
  // Clicking a leader toggles the series; show the hidden name struck through (not just greyed).
  itemHiddenStyle: { color: CONNECTIONS_COLORS.MUTED_HIDDEN, textDecoration: 'line-through' },
  labelFormatter(this: Highcharts.Point | Highcharts.Series) {
    const self = this as Highcharts.Series;
    const login = (self.options as { custom?: { login?: string } }).custom?.login || '';
    return avatarHTML(login, self.name);
  }
};

const sumMonths = (series: MonthValue[], pred: (m: string) => boolean): number =>
  series.filter((p) => pred(p.month)).reduce((s, p) => s + p.value, 0);

interface JoinRow {
  login: string;
  name: string;
  conn: number;
  attrition: number | null;
  headcountChange: number | null;
  headcountChangePct: number | null;
  headcount: number | null;
  prevFy: number | null;
  currentFy: number | null;
}

/**
 * "Connection intelligence x attrition" — replicates Saurabh's Canary retention
 * narrative on the live TM1 pipeline, scoped to the focus leader's directs.
 * Six Highcharts views (matching his layout): Connection vs Attrition, Attrition
 * Trend, Net Internal Movement, Connection vs Headcount Change, Headcount
 * Trajectory, Attrition Acceleration. The connection<->attrition join is keyed on
 * leader_login (fixing Saurabh's name-based join that dropped renamed/reorged teams).
 */
/**
 * Per-chart "what it shows / how to read it" copy for the Info popover on each card.
 * Keyed by the exact card title string passed to card().
 */
const CHART_INFO: Record<string, { what: string; read: string[] }> = {
  'Connection vs Attrition': {
    what: 'Flight-risk quadrant. Each bubble is a direct leader: x = avg connection score %, y = FY attrition %, bubble size = team size. Dashed lines are the org medians.',
    read: [
      'Top-left (below-median connection + above-median attrition) is the highest retention risk.',
      'Red bubbles are the flagged flight-risk leaders; blue are the rest.',
      'Bigger bubble = larger team, so the same position carries more people at stake.'
    ]
  },
  'Attrition Trend (Rolling 12 Months)': {
    what: 'Monthly attrition % per direct over the latest 12 months, crossing the calendar-year boundary for a full seasonal cycle. All attrition (regretted split not available).',
    read: [
      'Each line is one leader; click a name in the legend to show/hide it.',
      'A sustained upward slope signals worsening attrition; a single peak is usually a one-off.',
      'A gap means that leader has no attrition data for that month.'
    ]
  },
  'Net Internal Movement': {
    what: 'Net internal head movement (internal starts in − out) per leader. Blue = prior full FY, green = current FY year-to-date.',
    read: [
      'Bars to the right of zero = net talent gain (a magnet); to the left = net loss (an exporter).',
      'Compare blue vs green to gauge momentum — green near blue means the movement is recent.',
      'Sorted by current-FY net movement, highest at the top.'
    ]
  },
  'Connection Score vs Headcount Change': {
    what: 'Health × growth quadrant: x = avg connection score %, y = headcount growth % (change ÷ baseline). Dashed lines are the averages.',
    read: [
      'Bottom-left (low connection + shrinking) = active crisis; bottom-right (high connection but shrinking) = dilution risk.',
      'Growth is normalized to %, so large and small orgs are directly comparable.',
      'The 0% line separates growing (above) from shrinking (below).'
    ]
  },
  'Headcount Trajectory': {
    what: 'Ending active headcount over the full history (2025 → present) for each direct. Excludes the leader in focus; not period-scoped.',
    read: [
      'The slope shows growth/shrink pace; a flat line is a stable team.',
      'Compare relative trajectories rather than absolute size (teams differ in scale).',
      'Click a name in the legend to isolate a single leader.'
    ]
  },
  'Attrition Acceleration': {
    what: 'Floating bar per leader from prior-FY average monthly attrition % to current-FY average. Both endpoints are labeled.',
    read: [
      'Red bars = attrition rising year-over-year (accelerating); green = improving.',
      'Bar length = size of the FY-over-FY shift — a longer red bar means faster acceleration.',
      'Use it to rank who is deteriorating fastest, independent of absolute level.'
    ]
  }
};

/** Build the Header `info` popover for a chart card, or undefined if no copy exists. */
const chartInfoNode = (title: string): React.ReactNode => {
  const info = CHART_INFO[title];
  if (!info) return undefined;
  return <ChartInfoPopover title={title} what={info.what} read={info.read} />;
};

export const HeadcountInsights: React.FC<HeadcountInsightsProps> = ({ leaders, scorecards, startDate, sectionContext, infoLink }) => {
  const { data, loading, error } = useMultiLeaderHeadcount(leaders, startDate || null, null);
  const { isDarkMode } = useTheme();
  // High-contrast label text per theme (matches SentimentDeepDive / QuestionResponseBars).
  const labelColor = isDarkMode ? '#e8ecff' : '#1a2236';
  // Dynamic FY comparison: current = latest year present in the data, prev = current - 1.
  // Rolls forward automatically (e.g. FY27 vs FY26 once 2027 data loads).
  const currentYear = useMemo(() => latestYear(data || []), [data]);
  const prevYear = currentYear - 1;
  const fyLabel = (y: number) => `FY${String(y).slice(2)}`;
  // These charts are full-history / leader-scoped (not period-driven), so the top-right
  // label shows the focus leader only -- no period window.
  const focusName = leaders[0]?.name || '';

  const flows: LeaderPeopleFlow[] = useMemo(() => {
    const names = Object.fromEntries(leaders.map((l) => [l.login, l.name]));
    return buildPeopleFlows(data || [], names);
  }, [data, leaders]);

  const scoreByLogin = useMemo(() => {
    const m = new Map<string, number>();
    (scorecards || []).forEach((sc) => {
      const s = connectionScore(sc);
      if (sc.leaderLogin && s != null) m.set(sc.leaderLogin, s);
    });
    return m;
  }, [scorecards]);

  const join: JoinRow[] = useMemo(
    () =>
      flows
        .map((f) => {
          const conn = scoreByLogin.get(f.login);
          if (conn == null) return null;
          return {
            login: f.login,
            name: f.name,
            conn,
            attrition: f.currentFyAttrition ?? f.latestAttrition,
            headcountChange: f.headcountChange6mo,
            headcountChangePct: f.headcountChangePct,
            headcount: f.latestHeadcount,
            prevFy: f.prevFyAttrition,
            currentFy: f.currentFyAttrition
          } as JoinRow;
        })
        .filter((r): r is JoinRow => r !== null),
    [flows, scoreByLogin]
  );

  const connMed = useMemo(() => median(join.map((d) => d.conn)), [join]);
  const attrMed = useMemo(() => median(join.map((d) => d.attrition).filter((v): v is number => v != null)), [join]);
  const connAvgHc = connMed;

  const AXIS_TITLE_STYLE = { fontSize: '13px' };

  const dashLine = (value: number | null | undefined, label?: string, axis: 'x' | 'y' = 'x'): Highcharts.XAxisPlotLinesOptions[] =>
    value != null
      ? [
          {
            value,
            color: LABEL,
            dashStyle: 'Dash',
            width: 1,
            label: label
              ? {
                  text: label,
                  style: { color: labelColor, fontSize: '10px' },
                  rotation: 0,
                  // Pin the label to a plot edge (bottom for the vertical x-line, right for the
                  // horizontal y-line) so it never sits on top of a bubble at the crosshair.
                  ...(axis === 'x'
                    ? ({ verticalAlign: 'bottom', textAlign: 'center', y: -4 } as const)
                    : ({ align: 'right', textAlign: 'right', x: -4, y: -6 } as const))
                }
              : undefined
          }
        ]
      : [];

  // Plain dataLabels literal (no PlotScatterOptions type) so it's assignable to both scatter and bubble.
  const nameDataLabels = {
    enabled: true,
    format: '{point.name}',
    style: { color: labelColor, fontSize: '10px', fontWeight: '500', textOutline: 'none' },
    allowOverlap: true
  };

  // 1) Connection vs Attrition (flight-risk quadrant)
  const flightRiskOptions = useMemo<Highcharts.Options>(() => {
    const isDanger = (d: JoinRow) => connMed != null && attrMed != null && d.conn < connMed && (d.attrition as number) > attrMed;
    const pts = join.filter((d) => d.attrition != null);
    const toPoint = (d: JoinRow) => ({ x: d.conn, y: d.attrition as number, z: d.headcount ?? 1, name: d.name });
    const zVals = pts.map((d) => d.headcount ?? 1);
    const zMin = Math.min(...zVals, 1);
    const zMax = Math.max(...zVals, 1);
    return {
      chart: { type: 'bubble' },
      xAxis: {
        title: { text: 'Avg connection score (%)', style: AXIS_TITLE_STYLE },
        plotLines: dashLine(connMed, 'median', 'x'),
        labels: { format: '{value:.2f}' }
      },
      yAxis: {
        title: { text: `${fyLabel(currentYear)} attrition %`, style: AXIS_TITLE_STYLE },
        plotLines: dashLine(attrMed, 'median', 'y'),
        labels: { format: '{value:.2f}' }
      },
      tooltip: {
        pointFormat: `<b>{point.name}</b><br/>Connection: {point.x:.1f}%<br/>${fyLabel(
          currentYear
        )} attrition: {point.y:.2f}%<br/>Team size: {point.z}`
      },
      legend: { enabled: true },
      plotOptions: { bubble: { minSize: 6, maxSize: 44, sizeBy: 'area', zMin, zMax, dataLabels: nameDataLabels } },
      series: [
        { type: 'bubble', name: 'Flight risk (low connection · high attrition)', color: NEG, data: pts.filter(isDanger).map(toPoint) },
        { type: 'bubble', name: 'Other leaders', color: ACCENT, data: pts.filter((d) => !isDanger(d)).map(toPoint) }
      ]
    };
  }, [join, connMed, attrMed, labelColor, currentYear]);

  // 2) Attrition Trend (rolling last 12 months, monthly %) — spline per leader
  const trendLeaders = useMemo(() => flows.filter((f) => f.attritionMonthly.length).slice(0, MAX_LINES), [flows]);
  const attritionTrendOptions = useMemo<Highcharts.Options>(() => {
    // Rolling last 12 months across the data (crosses the calendar-year boundary for a full cycle).
    const months = Array.from(new Set(trendLeaders.flatMap((f) => f.attritionMonthly.map((m) => m.month))))
      .sort()
      .slice(-12);
    return {
      chart: { type: 'spline' },
      xAxis: { categories: months.map((m) => m.slice(0, 7)) },
      yAxis: { title: { text: 'Monthly attrition %', style: AXIS_TITLE_STYLE }, labels: { format: '{value}%' }, min: 0 },
      tooltip: { valueDecimals: 2, valueSuffix: '%', shared: false },
      legend: photoLegend,
      series: trendLeaders.map((f, i) => ({
        type: 'spline',
        name: f.name,
        custom: { login: f.login },
        color: SERIES_PALETTE[i % SERIES_PALETTE.length],
        data: months.map((m) => {
          const hit = f.attritionMonthly.find((x) => x.month === m);
          return hit ? Number(hit.value.toFixed(2)) : null;
        })
      }))
    };
  }, [trendLeaders]);

  // 3) Net Internal Movement — prev FY (full year) vs current FY (year-to-date), horizontal bars
  const netRows = useMemo(() => {
    const inCurrent = (m: string) => m.startsWith(String(currentYear));
    const inPrev = (m: string) => m.startsWith(String(prevYear));
    return flows
      .map((f) => ({
        name: f.name,
        login: f.login,
        cur: Math.round(sumMonths(f.netInternalMonthly, inCurrent)),
        prev: Math.round(sumMonths(f.netInternalMonthly, inPrev))
      }))
      .filter((r) => r.cur !== 0 || r.prev !== 0)
      .sort((a, b) => b.cur - a.cur)
      .slice(0, MAX_LINES);
  }, [flows, currentYear, prevYear]);
  const netInternalOptions = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'bar' },
      xAxis: {
        categories: netRows.map((d) => d.name),
        labels: {
          useHTML: true,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const row = netRows.find((r) => r.name === this.value);
            return avatarHTML(row?.login || '', String(this.value));
          }
        }
      },
      yAxis: { title: { text: 'Net heads (In − Out)', style: AXIS_TITLE_STYLE }, plotLines: [{ value: 0, color: LABEL, width: 1 }] },
      tooltip: { shared: true },
      plotOptions: {
        bar: { borderRadius: 2, dataLabels: { enabled: true, inside: false, style: { color: labelColor, fontSize: '10px', textOutline: 'none' } } }
      },
      series: [
        { type: 'bar', name: `${fyLabel(prevYear)} (full year)`, color: CONNECTIONS_COLORS.NET_PREV_FY, data: netRows.map((d) => d.prev) },
        { type: 'bar', name: `${fyLabel(currentYear)} (YTD)`, color: POS, data: netRows.map((d) => d.cur) }
      ]
    }),
    [netRows, currentYear, prevYear, labelColor]
  );

  // 4) Connection vs Headcount Change (health x growth quadrant)
  const growthOptions = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'scatter' },
      xAxis: {
        title: { text: 'Avg Connection Score (%)', style: AXIS_TITLE_STYLE },
        plotLines: dashLine(connAvgHc, 'avg', 'x'),
        labels: { format: '{value:.2f}' }
      },
      yAxis: {
        title: { text: 'Headcount growth %', style: AXIS_TITLE_STYLE },
        labels: { format: '{value:.2f}%' },
        plotLines: [{ value: 0, color: LABEL, width: 1 }]
      },
      tooltip: { pointFormat: '<b>{point.name}</b><br/>Connection: {point.x:.1f}%<br/>Headcount growth: {point.y:.1f}%' },
      legend: { enabled: false },
      plotOptions: { scatter: { dataLabels: nameDataLabels } },
      series: [
        {
          type: 'scatter',
          name: 'Leaders',
          data: join
            .filter((d) => d.headcountChangePct != null)
            .map((d) => ({ x: d.conn, y: d.headcountChangePct as number, name: d.name, color: (d.headcountChangePct as number) < 0 ? NEG : POS })),
          marker: { radius: 6 }
        }
      ]
    }),
    [join, connAvgHc, labelColor]
  );

  // 5) Headcount Trajectory — spline per leader (full history)
  const trajLeaders = useMemo(
    () =>
      flows
        .filter((f) => f.headcountMonthly.length)
        .sort((a, b) => (b.latestHeadcount || 0) - (a.latestHeadcount || 0))
        .slice(0, MAX_LINES),
    [flows]
  );
  const trajectoryOptions = useMemo<Highcharts.Options>(() => {
    const months = Array.from(new Set(trajLeaders.flatMap((f) => f.headcountMonthly.map((m) => m.month)))).sort();
    return {
      chart: { type: 'spline' },
      xAxis: { categories: months.map((m) => m.slice(0, 7)) },
      yAxis: { title: { text: 'Headcount', style: AXIS_TITLE_STYLE }, min: 0 },
      legend: photoLegend,
      series: trajLeaders.map((f, i) => ({
        type: 'spline',
        name: f.name,
        custom: { login: f.login },
        color: SERIES_PALETTE[i % SERIES_PALETTE.length],
        data: months.map((m) => {
          const hit = f.headcountMonthly.find((x) => x.month === m);
          return hit ? hit.value : null;
        })
      }))
    };
  }, [trajLeaders]);

  // 6) Attrition Acceleration — prev FY -> current FY floating range bar (inverted columnrange)
  const accelRows = useMemo(
    () =>
      join
        .filter((d) => d.prevFy != null && d.currentFy != null)
        .sort((a, b) => (b.currentFy as number) - (b.prevFy as number) - ((a.currentFy as number) - (a.prevFy as number))),
    [join]
  );
  const accelerationOptions = useMemo(
    () =>
      ({
        chart: { type: 'columnrange', inverted: true },
        xAxis: {
          categories: accelRows.map((d) => d.name),
          labels: {
            useHTML: true,
            formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
              const row = accelRows.find((r) => r.name === this.value);
              return avatarHTML(row?.login || '', String(this.value));
            }
          }
        },
        yAxis: { title: { text: 'Monthly attrition %', style: AXIS_TITLE_STYLE }, labels: { format: '{value}%' }, min: 0 },
        legend: { enabled: false },
        tooltip: {
          formatter(this: any): string {
            const r = accelRows[this.point.index];
            return `<b>${r.name}</b><br/>${fyLabel(prevYear)}: ${(r.prevFy as number).toFixed(2)}%<br/>${fyLabel(currentYear)}: ${(
              r.currentFy as number
            ).toFixed(2)}%<br/>Δ ${((r.currentFy as number) - (r.prevFy as number)).toFixed(2)} pts`;
          }
        },
        plotOptions: {
          columnrange: {
            borderRadius: 3,
            pointWidth: 14,
            dataLabels: {
              enabled: true,
              inside: false,
              crop: false,
              overflow: 'allow',
              format: '{y:.2f}%',
              style: { color: labelColor, fontSize: '10px', textOutline: 'none' }
            }
          }
        },
        series: [
          {
            type: 'columnrange',
            name: `${fyLabel(prevYear)} → ${fyLabel(currentYear)}`,
            data: accelRows.map((d) => {
              const lo = Math.min(d.prevFy as number, d.currentFy as number);
              const hi = Math.max(d.prevFy as number, d.currentFy as number);
              return { low: Number(lo.toFixed(2)), high: Number(hi.toFixed(2)), color: (d.currentFy as number) >= (d.prevFy as number) ? NEG : POS };
            })
          }
        ]
      } as Highcharts.Options),
    [accelRows, labelColor, currentYear, prevYear]
  );

  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="big" /> Loading headcount & attrition...
      </Box>
    );
  }
  if (error) {
    return (
      <Alert type="error" header="Failed to load headcount & attrition">
        {error.message}
      </Alert>
    );
  }
  if (!flows.length || !join.length) {
    return (
      <Box color="text-body-secondary" padding="m">
        No headcount / attrition data available for this leader and their directs.
      </Box>
    );
  }

  const card = (icon: string, title: string, subtitle: string, chart: React.ReactNode, contextLabel: string) => (
    <Container
      key={title}
      header={
        <Header
          variant="h2"
          info={chartInfoNode(title) || infoLink}
          description={subtitle}
          actions={contextLabel ? <span className="cd-section-context">{contextLabel}</span> : undefined}
        >
          {icon} {title}
        </Header>
      }
    >
      {chart}
    </Container>
  );

  return (
    <ColumnLayout columns={2}>
      {[
        card(
          '🎯',
          'Connection vs Attrition',
          'Flight-Risk Quadrant — top-left (low connection, high attrition) is highest retention risk; bubble size = team size',
          <HighchartsChart options={flightRiskOptions} height={CHART_H} />,
          sectionContext || focusName
        ),
        card(
          '📈',
          'Attrition Trend (Rolling 12 Months)',
          'Monthly % · all attrition (regretted split not available)',
          <HighchartsChart options={attritionTrendOptions} height={CHART_H} />,
          focusName
        ),
        card(
          '🔬',
          'Connection Score vs Headcount Change',
          'Health × Growth Quadrant — bottom-left is active crisis, bottom-right is dilution risk',
          <HighchartsChart options={growthOptions} height={CHART_H} />,
          sectionContext || focusName
        ),
        netRows.length > 0
          ? card(
              '🔄',
              'Net Internal Movement',
              `${fyLabel(prevYear)} (full year) vs ${fyLabel(currentYear)} (YTD) · net heads (In − Out)`,
              <HighchartsChart options={netInternalOptions} height={CHART_H} />,
              focusName
            )
          : null,
        card(
          '👥',
          'Headcount Trajectory',
          'Team size over time (excludes app-usage/trend metrics)',
          <HighchartsChart options={trajectoryOptions} height={CHART_H} />,
          focusName
        ),
        accelRows.length > 0
          ? card(
              '⚡',
              'Attrition Acceleration',
              `${fyLabel(prevYear)} → ${fyLabel(currentYear)} · avg monthly attrition shift per leader`,
              <HighchartsChart options={accelerationOptions} height={CHART_H} />,
              focusName
            )
          : null
      ].filter(Boolean)}
    </ColumnLayout>
  );
};
